import React, { useEffect, useState } from "react";
import { Flex, Modal, Spin, Typography } from "antd";
import styled from "styled-components";
import { pageBleedCss } from "components/layout/pagePadding";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useInvalidate, useTranslate } from "@refinedev/core";
import {
  OutOfDatePublicationWidget,
  OutOfDatePublicationCompactButton,
  ReadyModal,
  radius,
} from "@libertify/ui";
import { CreatorPreview } from "./CreatorPreview";
import { CreatorToolbar } from "./CreatorToolbar";
import { CreatorToolbarMobile } from "./CreatorToolbarMobile";
import { CreatorFooter } from "./CreatorFooter";
import { useGetActiveSubscriptions } from "pages/pricing/hooks/useGetActiveSubscriptions/";
import { ProjectSettingsDrawer } from "./ProjectSettingsDrawer";
import { ProjectPublish } from "./ProjectPublish";
import { ProjectSecondaryMenu } from "./ProjectSecondaryMenu";
import useIsAdmin from "hooks/useIsAdmin";
import { SlideCarouselPreview } from "./SlideCarouselPreview";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useSlideCarouselData } from "../hooks/useSlideCarouselData";
import type { ProjectPageDetails } from "./ProjectPage";
import { useOutOfDateDismissal } from "../hooks/useOutOfDateDismissal";
import { QuotaUpgradeModal } from "components/QuotaUpgradeModal";
import { useQuota } from "hooks/useQuota";
import { QuotaFeatures } from "@/constants";
import {
  isProjectHeyGenius,
  computeIsMediaGenerating,
} from "../helpers/helper";
import { HeyGeniusPreview } from "../heygenius-preview";

export type ProjectPageNewProps = {
  projectDetails: ProjectPageDetails;
  onUnpublish: () => Promise<void>;
  onPublish: () => Promise<{ quotaExceeded: boolean }>;
  refetchProjectDetail: () => void;
};

export const ProjectPageNew: React.FC<ProjectPageNewProps> = ({
  projectDetails,
  onUnpublish,
  onPublish,
  refetchProjectDetail,
}) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const isAdmin = useIsAdmin();
  const { isBlocked } = useQuota();
  const { isFreeUser } = useGetActiveSubscriptions();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [quotaModalOpen, setQuotaModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const showReadyModal = searchParams.has("newExperience");

  // Audience view toggles — lifted here so both drawer and preview react immediately
  const settings = projectDetails.project.settings;
  const [documentEnabled, setDocumentEnabled] = useState(
    () => settings.include_document_animations !== false
  );
  const [chatEnabled, setChatEnabled] = useState(
    () => settings.chat_mode !== "Disabled"
  );
  const [previewKey, setPreviewKey] = useState(0);

  const handleCloseReadyModal = () => {
    searchParams.delete("newExperience");
    setSearchParams(searchParams, { replace: true });
  };

  const handleShareClick = () => {
    if (!isLive && isBlocked(QuotaFeatures.ProjectPublication)) {
      setQuotaModalOpen(true);
      return;
    }
    setShareModalOpen(true);
  };

  const handleReadyModalShare = () => {
    handleCloseReadyModal();
    handleShareClick();
  };
  const {
    project,
    projectId,
    instanceId,
    storyboardId,
    media,
    organizationSlug,
    publishedPublicationUrl,
    isLive,
    isDocHeyGenius,
    isUnpublishing,
    isPublicationOutOfDate,
    isMediaOutOfDate,
    isOutOfDate,
    isOutOfDateStatusLoading,
    onRepublish,
    onRegenerate,
  } = projectDetails;

  // Once the user picks "Regenerate later"/"Update later", the floating
  // widget stays dismissed across reloads and the compact button takes its
  // place next to Share (see MED-6969).
  const {
    isDismissed: isOutOfDateDismissed,
    dismiss: dismissOutOfDateWidget,
    handleRegenerate: handleRegenerateOutOfDate,
    handleUpdate: handleUpdateOutOfDate,
  } = useOutOfDateDismissal({
    projectId,
    onRegenerate,
    onUpdate: onRepublish,
  });

  // URL-based HeyGenius projects (type includes "hey_genius") render the
  // interactive animated web page instead of the video/PDF/chat experience.
  const isHeyGenius = isProjectHeyGenius(project.type);

  const isMediaGenerating = computeIsMediaGenerating(
    project.status,
    media?.status
  );

  const readyProjectStatuses = ["Published", "MediaGenerated", "New"];
  const readyMediaStatuses = ["Ready", "Generated"];

  const isMediaReady = Boolean(
    instanceId &&
    media?.id &&
    media.url &&
    (readyProjectStatuses.includes(project.status ?? "") ||
      readyMediaStatuses.includes(media.status))
  );

  const storyboardPendingStatuses = [
    "StoryboardGenerating",
    "StoryboardReviewing",
    "StoryboardApproved",
  ];
  const isStoryboardPending = storyboardPendingStatuses.includes(
    project.status ?? ""
  );

  const shouldShowGenerating = isMediaGenerating || isStoryboardPending;

  // HG Doc renders through InDocApp which doesn't display the slide carousel,
  // and its media settings carry the magic `chapter_template_id: "heygenius"`
  // which 404s in `GET /templates/{id}` — pinning useStreamingTemplates in a
  // permanent loading state. Skip the hook entirely for HG Doc.
  const shouldFetchCarousel = shouldShowGenerating && !isDocHeyGenius;

  const carouselData = useSlideCarouselData({
    project: shouldFetchCarousel ? project : undefined,
    projectId: shouldFetchCarousel ? projectId : undefined,
    storyboardId: shouldFetchCarousel ? storyboardId : undefined,
  });

  const hasCarouselSlides = carouselData.templates.size > 0;

  // When media finishes generating, invalidate the instance cache so
  // CreatorPreview fetches fresh data that includes the video URL.
  // Keep showing the carousel/spinner until the refetch completes.
  const invalidate = useInvalidate();
  const [prevIsMediaGenerating, setPrevIsMediaGenerating] =
    useState(isMediaGenerating);
  const [awaitingInstance, setAwaitingInstance] = useState(false);

  // Detect generating → ready transition during render using state
  // (not refs) to comply with React Compiler rules.
  if (prevIsMediaGenerating !== isMediaGenerating) {
    setPrevIsMediaGenerating(isMediaGenerating);
    if (prevIsMediaGenerating && !isMediaGenerating) {
      setAwaitingInstance(true);
    }
  }

  useEffect(() => {
    if (awaitingInstance && instanceId) {
      invalidate({
        resource: `media/projects/${projectId}/instances`,
        id: instanceId,
        invalidates: ["detail"],
      })
        .then(() => setAwaitingInstance(false))
        .catch((error) => {
          console.error("Error invalidating instance:", error);
        });
    }
  }, [awaitingInstance, invalidate, projectId, instanceId]);

  if (
    !isHeyGenius &&
    !isMediaReady &&
    !isMediaGenerating &&
    !isStoryboardPending
  ) {
    return (
      <FullHeightContainer>
        <StyledCenteredContent>
          <Spin size="large" />
        </StyledCenteredContent>
      </FullHeightContainer>
    );
  }

  const secondaryMenu =
    isAdmin && storyboardId && instanceId ? (
      <ProjectSecondaryMenu
        project={project}
        storyboardId={storyboardId}
        instanceId={instanceId}
      />
    ) : undefined;

  const toolbarProps = {
    publishedPublicationUrl,
    isLive,
    isHeyGenius,
    canEditVideo: !isDocHeyGenius,
    onAnalyticsClick: () => {
      const target = `/media/projects/${projectId}/analytics`;
      if (window.location.pathname === target) return;
      navigate(target);
    },
    onEditVideoClick: () => {
      const target = `/media/projects/${projectId}/${storyboardId}/${instanceId}/storyboard/editor`;
      if (window.location.pathname === target) return;
      navigate(target);
    },
    onEditClick: () => setDrawerOpen(true),
    onShareClick: handleShareClick,
    // Compact stand-in for the floating widget, once dismissed for this
    // project — same action, no confirmation step. Rendered right after Share.
    outOfDateAction:
      isOutOfDate && isOutOfDateDismissed ? (
        <OutOfDatePublicationCompactButton
          isLoading={isOutOfDateStatusLoading}
          isItemsOutOfDate={isMediaOutOfDate}
          isPublicationOutOfDate={isPublicationOutOfDate}
          onRegenerate={handleRegenerateOutOfDate}
          onUpdate={handleUpdateOutOfDate}
        />
      ) : undefined,
    secondaryMenu,
    isUnpublishing,
    onPublishToggle: (checked: boolean) => {
      if (checked) {
        void onPublish().then((result) => {
          if (result.quotaExceeded) {
            setDrawerOpen(false);
          }
        });
      } else {
        void onUnpublish();
      }
    },
  };

  const showOverride =
    isMediaGenerating || isStoryboardPending || awaitingInstance;
  const carouselOverride = showOverride ? (
    hasCarouselSlides ? (
      <SlideCarouselPreview
        sections={carouselData.sections}
        templates={carouselData.templates}
        sectionTemplateIds={carouselData.sectionTemplateIds}
        brandKitData={carouselData.brandKitData}
        mediaAspectRatio={carouselData.mediaAspectRatio}
      />
    ) : (
      <StyledCenteredContent>
        <Spin size="large" />
        {isStoryboardPending && (
          <Typography.Text
            style={{
              marginTop: 12,
              fontSize: 13,
              textAlign: "center",
              color: "rgba(255,255,255,0.6)",
              maxWidth: 300,
            }}
          >
            {t("media.projects.detail.storyboardGeneratingHint")}
          </Typography.Text>
        )}
      </StyledCenteredContent>
    )
  ) : undefined;

  const outOfDateWidget =
    isOutOfDate && !isOutOfDateDismissed ? (
      <OutOfDatePublicationWidget
        isLoading={isOutOfDateStatusLoading}
        isItemsOutOfDate={isMediaOutOfDate}
        isPublicationOutOfDate={isPublicationOutOfDate}
        onRegenerate={handleRegenerateOutOfDate}
        onUpdate={handleUpdateOutOfDate}
        onDismiss={dismissOutOfDateWidget}
      />
    ) : undefined;

  const overlays = (
    <>
      <ProjectSettingsDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        projectDetails={projectDetails}
        refetchProjectDetail={refetchProjectDetail}
        onShareClick={handleShareClick}
        chatEnabled={chatEnabled}
        documentEnabled={documentEnabled}
        onChatToggle={setChatEnabled}
        onDocumentToggle={setDocumentEnabled}
        onPreviewRefresh={() => setPreviewKey((k) => k + 1)}
        isMediaReady={isMediaReady}
      />
      <Modal
        open={shareModalOpen}
        onCancel={() => setShareModalOpen(false)}
        width={520}
        footer={null}
        closable
        maskClosable
        centered
        destroyOnClose
        zIndex={1100}
        styles={{ body: { padding: 0 } }}
        style={{ borderRadius: radius.box, overflow: "hidden" }}
      >
        <ProjectPublish
          projectDetails={{
            projectId,
            instanceId,
            organizationId: project.organization_id,
          }}
          onCancel={() => setShareModalOpen(false)}
          isLive={isLive}
          publicationUrl={publishedPublicationUrl}
        />
      </Modal>
      <ReadyModal
        open={showReadyModal}
        onClose={handleCloseReadyModal}
        onShare={handleReadyModalShare}
        title={t("media.components.ProjectPublish.readyModalTitle")}
        subtitle={t("media.components.ProjectPublish.readyModalSubtitle")}
        viewLabel={t("media.components.ProjectPublish.viewExperience")}
        shareLabel={t("media.components.ProjectPublish.shareWithOthers")}
      />
      <QuotaUpgradeModal
        open={quotaModalOpen}
        onClose={() => setQuotaModalOpen(false)}
      />
      {outOfDateWidget}
    </>
  );

  if (isHeyGenius) {
    return (
      <FullHeightContainer>
        <Flex vertical style={{ height: "100%" }}>
          {!isMobile ? (
            <CreatorToolbar {...toolbarProps} />
          ) : (
            <CreatorToolbarMobile {...toolbarProps} />
          )}
          <Flex flex={1} style={{ minHeight: 0 }}>
            <HeyGeniusPreview projectId={projectId} />
          </Flex>
        </Flex>
        {overlays}
      </FullHeightContainer>
    );
  }

  return (
    <FullHeightContainer>
      <CreatorPreview
        key={previewKey}
        projectId={projectId}
        instanceId={instanceId}
        mediaId={media?.id ?? undefined}
        organizationSlug={organizationSlug}
        instanceSlug={media?.instance_slug ?? undefined}
        creatorToolbar={
          !isMobile ? (
            <CreatorToolbar {...toolbarProps} />
          ) : (
            <CreatorToolbarMobile {...toolbarProps} />
          )
        }
        creatorToolbarMobile={<CreatorToolbarMobile {...toolbarProps} />}
        creatorFooter={!isMobile && isFreeUser ? <CreatorFooter /> : undefined}
        chatEnabled={chatEnabled}
        documentEnabled={documentEnabled}
        videoOverride={carouselOverride}
        isDocHeyGenius={projectDetails.isDocHeyGenius}
        isAudioOnly={projectDetails.isAudioOnly}
        isMediaGenerating={isMediaGenerating || isStoryboardPending}
        project={project}
        media={media}
      />
      {overlays}
    </FullHeightContainer>
  );
};

const StyledCenteredContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const FullHeightContainer = styled.div`
  /* Cancel the app shell's page padding (full-bleed editor) */
  ${({ theme }) => pageBleedCss(theme)}
  height: 100vh;
  height: 100dvh;
  position: relative;
  overflow: hidden;

  @media (width <= 576px) {
    /* Fixed positioning avoids negative-margin horizontal overflow and
       lets MobileScreenSwap's fixed Root fill the viewport cleanly. */
    position: fixed;
    inset: 0;
    margin: 0;
    height: 100dvh;
    height: -webkit-fill-available;
  }
`;
